/**
 * Layout primitives (DS foundation): PageHeader, Section, DetailLayout, TabPanel, Disclosure, DefinitionList,
 * SkeletonText and StatusBadge. Fluid (rem / clamp / container queries), tokens only (ds.css).
 */
import { useId, useState, type ReactNode } from "react";
import { Badge, Skeleton, type BadgeProps } from "./components.js";
import { statusBadge, type StatusDomain } from "./status-map.js";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

export type PageHeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** A back link above the title (pass the router's <Link>). */
  back?: ReactNode;
  /** Small mono label above the title ("CUSTOMER", "SHIPMENT"). */
  eyebrow?: ReactNode;
  /** Badges beside the title (status, prospect). */
  badges?: ReactNode;
  /** The one primary action (rightmost). */
  primaryAction?: ReactNode;
  /** Secondary actions, left of the primary (buttons or a Menu for overflow). */
  secondaryActions?: ReactNode;
  /** Facts under the subtitle (a row of "label value" pairs). */
  meta?: ReactNode;
  className?: string;
};

/** Page title block: back link, eyebrow, title + badges, subtitle, meta, and actions (one primary, secondaries before it). */
export function PageHeader({ title, subtitle, back, eyebrow, badges, primaryAction, secondaryActions, meta, className }: PageHeaderProps) {
  return (
    <header className={cx("ffx-pagehead", className)}>
      {back ? <div className="ffx-pagehead__back">{back}</div> : null}
      <div className="ffx-pagehead__row">
        <div className="ffx-pagehead__text">
          {eyebrow ? <p className="ffx-pagehead__eyebrow">{eyebrow}</p> : null}
          <div className="ffx-pagehead__titlerow">
            <h1 className="ffx-pagehead__title">{title}</h1>
            {badges ? <span className="ffx-pagehead__badges">{badges}</span> : null}
          </div>
          {subtitle ? <p className="ffx-pagehead__sub">{subtitle}</p> : null}
          {meta ? <div className="ffx-pagehead__meta">{meta}</div> : null}
        </div>
        {primaryAction || secondaryActions ? (
          <div className="ffx-pagehead__actions">
            {secondaryActions}
            {primaryAction}
          </div>
        ) : null}
      </div>
    </header>
  );
}

export type SectionProps = {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** No body padding (a table or list that runs edge to edge). */
  flush?: boolean;
  /** Render as a <fieldset> (form sections); `disabled` then disables every control inside. */
  fieldset?: boolean;
  disabled?: boolean;
  /** Heading level for the title (default 2). */
  level?: 2 | 3;
  className?: string;
  id?: string;
};

/** A titled panel: the console/portal card surface with a header row (title, description, actions) and a body. */
export function Section({ title, description, actions, children, flush, fieldset, disabled, level = 2, className, id }: SectionProps) {
  const auto = useId();
  const headId = `${id ?? auto}-title`;
  const H = level === 3 ? "h3" : "h2";
  const head =
    title || actions || description ? (
      <div className="ffx-section__head">
        <div className="ffx-section__titles">
          {title ? <H className="ffx-section__title" id={headId}>{title}</H> : null}
          {description ? <p className="ffx-section__desc">{description}</p> : null}
        </div>
        {actions ? <div className="ffx-section__actions">{actions}</div> : null}
      </div>
    ) : null;
  const body = <div className={cx("ffx-section__body", flush && "ffx-section__body--flush")}>{children}</div>;
  if (fieldset) {
    // The <legend> must be the fieldset's first child to name the group; description and actions follow it.
    return (
      <fieldset className={cx("ffx-section ffx-section--fieldset", className)} id={id} disabled={disabled}>
        {title ? <legend className="ffx-section__title ffx-section__legend">{title}</legend> : null}
        {description || actions ? (
          <div className="ffx-section__head ffx-section__head--sub">
            {description ? <p className="ffx-section__desc">{description}</p> : <span />}
            {actions ? <div className="ffx-section__actions">{actions}</div> : null}
          </div>
        ) : null}
        {body}
      </fieldset>
    );
  }
  return (
    <section className={cx("ffx-section", className)} id={id} aria-labelledby={title ? headId : undefined}>
      {head}
      {body}
    </section>
  );
}

export type DetailLayoutProps = {
  /** A PageHeader. */
  header: ReactNode;
  /** KPIs or a status strip under the header (optional). */
  summary?: ReactNode;
  /** Banners about this record (active sessions, holds). */
  notices?: ReactNode;
  /** The Tabs for this record. */
  tabs?: ReactNode;
  /** The active tab's content. */
  children: ReactNode;
  /** Side panel: key facts and quick actions (Sections). Beside the content from 64rem of container width, below it under. */
  aside?: ReactNode;
  asideLabel?: string;
  className?: string;
};

/**
 * Detail page frame for customers, shipments, quotes, tickets and carriers: header, optional summary and notices,
 * tabs, then the tab content beside a side panel. The panel is a labelled <aside> (complementary landmark).
 */
export function DetailLayout({ header, summary, notices, tabs, children, aside, asideLabel = "Details", className }: DetailLayoutProps) {
  return (
    <div className={cx("ffx-detail", aside ? "ffx-detail--aside" : null, className)}>
      {header}
      {notices ? <div className="ffx-detail__notices">{notices}</div> : null}
      {summary ? <div className="ffx-detail__summary">{summary}</div> : null}
      <div className="ffx-detail__grid">
        <div className="ffx-detail__main">
          {tabs ? <div className="ffx-detail__tabs">{tabs}</div> : null}
          {children}
        </div>
        {aside ? (
          <aside className="ffx-detail__aside" aria-label={asideLabel}>
            {aside}
          </aside>
        ) : null}
      </div>
    </div>
  );
}

/** Tab ids shared by `Tabs` (pass `idBase`) and `TabPanel`, so each tab controls its panel and the panel is named by its tab. */
export const tabId = (base: string, value: string) => `${base}-tab-${value}`;
export const panelId = (base: string) => `${base}-panel`;
export function TabPanel({ idBase, value, children, className }: { idBase: string; value: string; children: ReactNode; className?: string }) {
  return (
    <div role="tabpanel" id={panelId(idBase)} aria-labelledby={tabId(idBase, value)} tabIndex={0} className={cx("ffx-tabpanel", className)} key={value}>
      {children}
    </div>
  );
}

/** Expandable block: a button with a turning chevron; the content fades and settles in (transform/opacity only). */
export function Disclosure({ summary, children, defaultOpen = false, open: openProp, onOpenChange, className }: { summary: ReactNode; children: ReactNode; defaultOpen?: boolean; open?: boolean; onOpenChange?: (open: boolean) => void; className?: string }) {
  const [inner, setInner] = useState(defaultOpen);
  const open = openProp ?? inner;
  const id = useId();
  const toggle = () => {
    const next = !open;
    if (openProp === undefined) setInner(next);
    onOpenChange?.(next);
  };
  return (
    <div className={cx("ffx-disclosure", open && "is-open", className)}>
      <button type="button" className="ffx-disclosure__btn" aria-expanded={open} aria-controls={id} onClick={toggle}>
        <svg className="ffc-chev ffx-disclosure__chev" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M6.25 4.5 9.75 8l-3.5 3.5" />
        </svg>
        <span>{summary}</span>
      </button>
      <div id={id} className="ffx-disclosure__body" hidden={!open}>
        {children}
      </div>
    </div>
  );
}

/** Label / value pairs (account facts in a side panel). Values wrap; long ids break anywhere. */
export function DefinitionList({ items, className }: { items: ReadonlyArray<{ term: ReactNode; detail: ReactNode; key?: string } | null | false>; className?: string }) {
  return (
    <dl className={cx("ffx-dl", className)}>
      {items.filter(Boolean).map((it, i) => {
        const x = it as { term: ReactNode; detail: ReactNode; key?: string };
        return (
          <div key={x.key ?? i} className="ffx-dl__row">
            <dt>{x.term}</dt>
            <dd>{x.detail}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/** Lines of placeholder text while a block loads (decorative; the region carries aria-busy). */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span className={cx("ffx-skeltext", className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? "58%" : i % 2 ? "86%" : "100%"} />
      ))}
    </span>
  );
}

/** A status as a badge, from the single status map: `<StatusBadge domain="shipment" value={s.status} />`. */
export function StatusBadge({ domain, value, size, dot }: { domain: StatusDomain; value: string | null | undefined } & Pick<BadgeProps, "size" | "dot">) {
  const s = statusBadge(domain, value);
  return (
    <Badge tone={s.tone} live={s.live} size={size} dot={dot}>
      {s.label}
    </Badge>
  );
}
